import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Col, Container, Form, Row, Spinner, Badge } from "react-bootstrap";
import DynamicBreadcrumb from "../header/breadcrumb";
import branchApi from "../../services/branchapi";
import orderLocationApi from "../../services/orderLocationApi";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";

const COLORS = [
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#9333ea",
  "#ea580c",
  "#0891b2",
  "#65a30d",
  "#db2777",
  "#0f766e",
  "#7c3aed",
  "#f59e0b",
  "#ef4444",
];

const getBranchDisplayName = (branch = {}) => {
  return (
    branch.name ||
    branch.branchName ||
    branch.branch_name ||
    branch.brch_name ||
    branch.zoneBranchName ||
    `Branch ${branch.id || branch.branchId || ""}`
  );
};

const getBranchColor = (branchId) => COLORS[Math.abs(Number(branchId || 0)) % COLORS.length];

const normalizeGeojson = (geojson) => {
  if (!geojson) return null;

  if (typeof geojson === "string") {
    try {
      return JSON.parse(geojson);
    } catch (e) {
      return null;
    }
  }

  return geojson;
};

const geojsonToPath = (geojson) => {
  const geo = normalizeGeojson(geojson);
  const ring = geo?.coordinates?.[0] || [];
  return ring.map(([lng, lat]) => ({
    lat: Number(lat),
    lng: Number(lng),
  }));
};

export default function BranchZonesOverview() {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const polygonsRef = useRef([]);
  const markersRef = useRef([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [zones, setZones] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");

  const breadcrumb = useMemo(
    () => [{ name: "Branches Zones Overview", url: null }],
    []
  );

  const filteredZones = useMemo(() => {
    const safeZones = Array.isArray(zones) ? zones : [];
    if (!selectedBranchId) return safeZones;
    return safeZones.filter((zone) => String(zone.branchId) === String(selectedBranchId));
  }, [zones, selectedBranchId]);

  const filteredBranches = useMemo(() => {
    const safeBranches = Array.isArray(branches) ? branches : [];
    if (!selectedBranchId) return safeBranches;
    return safeBranches.filter((branch) => String(branch.id) === String(selectedBranchId));
  }, [branches, selectedBranchId]);

  const clearMapObjects = () => {
    polygonsRef.current.forEach((polygon) => polygon.setMap(null));
    markersRef.current.forEach((marker) => marker.setMap(null));
    polygonsRef.current = [];
    markersRef.current = [];
    if (infoWindowRef.current) infoWindowRef.current.close();
  };

  const drawMap = async () => {
    const google = await loadGoogleMaps([]);
    if (!mapDivRef.current) return;

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapDivRef.current, {
        center: { lat: 13.0827, lng: 80.2707 },
        zoom: 10,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      infoWindowRef.current = new google.maps.InfoWindow();
    }

    clearMapObjects();

    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;

    // Draw polygons first
    (Array.isArray(filteredZones) ? filteredZones : []).forEach((zone) => {
      const path = geojsonToPath(zone.geojson);
      if (!path.length) return;

      const color = getBranchColor(zone.branchId);

      const polygon = new google.maps.Polygon({
        paths: path,
        map: mapRef.current,
        clickable: true,
        editable: false,
        draggable: false,
        strokeColor: color,
        fillColor: color,
        strokeOpacity: 0.95,
        fillOpacity: 0.18,
        strokeWeight: 3,
      });

      polygon.addListener("click", (event) => {
        infoWindowRef.current.setContent(`
          <div style="min-width:220px;line-height:1.5;">
            <div style="font-weight:700;">${zone.branchName ||
          zone.branch_name ||
          zone.brch_name ||
          `Branch ${zone.branchId || zone.branch_id || ""}`
          }</div>
            <div>Zone: ${zone.zoneName || zone.zoneType || "-"}</div>
            <div>Type: ${zone.zoneType || "-"}</div>
            <div>Priority: ${zone.priority ?? "-"}</div>
          </div>
        `);
        infoWindowRef.current.setPosition(event.latLng);
        infoWindowRef.current.open({ map: mapRef.current });
      });

      polygonsRef.current.push(polygon);

      path.forEach((point) => {
        bounds.extend(point);
        hasBounds = true;
      });
    });

    // Draw all branch markers always
    (Array.isArray(filteredBranches) ? filteredBranches : []).forEach((branch) => {
      if (!branch.latitude || !branch.longitude) return;

      const branchId = branch.id || branch.branchId;
      const branchName = getBranchDisplayName(branch);
      const color = getBranchColor(branchId);

      const marker = new google.maps.Marker({
        position: {
          lat: Number(branch.latitude),
          lng: Number(branch.longitude),
        },
        map: mapRef.current,
        title: branchName,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
          scale: 10,
        },
      });

      marker.addListener("click", () => {
        const zoneCount = filteredZones.filter(
          (z) => String(z.branchId || z.branch_id) === String(branchId)
        ).length;

        infoWindowRef.current.setContent(`
          <div style="min-width:220px;line-height:1.5;">
            <div style="font-weight:700;">${branchName}</div>
            <div>Branch ID: ${branchId || "-"}</div>
            <div>Latitude: ${branch.latitude || branch.branchLatitude || "-"}</div>
            <div>Longitude: ${branch.longitude || branch.branchLongitude || "-"}</div>
            <div>Mapped Zones: ${zoneCount}</div>
          </div>
        `);

        infoWindowRef.current.open({
          map: mapRef.current,
          anchor: marker,
        });
      });

      markersRef.current.push(marker);
      bounds.extend(marker.getPosition());
      hasBounds = true;
    });

    if (hasBounds) {
      mapRef.current.fitBounds(bounds);

      google.maps.event.addListenerOnce(mapRef.current, "bounds_changed", () => {
        if (mapRef.current.getZoom() > 14) {
          mapRef.current.setZoom(14);
        }
      });
    } else {
      mapRef.current.setCenter({ lat: 13.0827, lng: 80.2707 });
      mapRef.current.setZoom(10);
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");

    Promise.all([
      orderLocationApi.getZonesOverview({
        activeOnly: true,
        zoneType: null, // IMPORTANT: fetch all zone types
      }),
      branchApi.getAllBranches({
        limit: 500,
        page: 0,
      }),
    ])
      .then(([zoneResponse, branchResponse]) => {
        if (!mounted) return;

        const zonePayload = zoneResponse?.data;
        const safeZones = Array.isArray(zonePayload)
          ? zonePayload
          : Array.isArray(zonePayload?.result)
            ? zonePayload.result
            : [];

        const branchPayload = branchResponse?.data;
        const safeBranches = Array.isArray(branchPayload)
          ? branchPayload
          : Array.isArray(branchPayload?.result)
            ? branchPayload.result
            : [];

        setZones(safeZones);
        setBranches(safeBranches);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.message || "Failed to load zones overview");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      drawMap();
    }
  }, [loading, filteredZones, filteredBranches]);

  return (
    <Container fluid className="py-3">
      <DynamicBreadcrumb pathArray={breadcrumb} />

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Filter by Branch</Form.Label>
            <Form.Select
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value)}
            >
              <option value="">All Branches</option>
              {branches.map((branch, index) => {
                const optionValue = branch.id || branch.branchId || "";
                const optionKey = optionValue || `branch-${index}`;
                const label = getBranchDisplayName(branch);

                return (
                  <option key={optionKey} value={optionValue}>
                    {label}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={8} className="d-flex align-items-end gap-3 flex-wrap">
          <div className="small text-muted">
            Showing <b>{filteredZones.length}</b> zone(s)
          </div>
          <Badge bg="secondary">Branches: {filteredBranches.length}</Badge>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <div
          ref={mapDivRef}
          style={{
            width: "100%",
            height: 700,
            borderRadius: 14,
            overflow: "hidden",
          }}
        />
      )}
    </Container>
  );
}